import DataTable from '@/components/features/data/DataTable'
import { userColumns } from '@/components/features/data/DataTable/ColumnDefinitions/UserColumns'
import { EditInvitationGroupsForm } from '@/components/features/organisations/invitations/EditInvitationGroupsForm'
import { invitationGroupNames } from '@/components/features/organisations/invitations/InvitationGroupsField'
import { InviteMemberForm } from '@/components/features/organisations/invitations/InviteMemberForm'
import { PendingInvitationsTable } from '@/components/features/organisations/invitations/PendingInvitationsTable'
import {
  orgInvitationErrorMessage,
  useOrgInvitations,
} from '@/components/features/organisations/invitations/useOrgInvitations'
import Modal from '@/components/layout/Modal'
import ConfirmationDialog from '@/components/utils/ConfirmationDialog'
import { useClient } from '@/context/ClientContext'
import { useOffsetPaginationQuery } from '@/hooks/useOffsetPaginationQuery'
import { useOrganisationUsersWithUserMap } from '@/hooks/useOrganisationUsersWithUserMap'
import { usePermissionCheck } from '@/hooks/usePermissionCheck'
import {
  fetchGroups,
  fetchOrganisationUsersPaginated,
} from '@/lib/clientHelpers'
import { getUserFullName } from '@/lib/utils'
import { EnvelopeIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { Button } from '@droidmap/frontend-ui'
import { Input } from '@droidmap/frontend-ui'
import { UpdateOrganisationUserInput } from '@droidmap/organisation-service-contract'
import type {
  OrganisationInvitation,
  OrganisationUser,
} from '@droidmap/organisation-service-contract'
import { Permission } from '@droidmap/permissions-service-contract'

const PREFERENCE_KEY = 'ORGANISATION_MEMBERS'

/**
 * Generate a unique ID for an OrganisationUser for use in the DataTable.
 * Combines orgId and userId to ensure uniqueness.
 */
const getOrganisationUserId = (
  orgUser: Pick<OrganisationUser, 'orgId' | 'userId'>,
): string => {
  return `${orgUser.orgId}-${orgUser.userId}`
}

type OrgUserWithId = OrganisationUser & { id: string }

export const Route = createFileRoute('/_authenticated/orgs/$orgId/members')({
  component: OrganisationUsers,
  loader: ({ context }) => ({
    crumb: 'Members',
    metadata: context.metadata,
  }),
})

function OrganisationUsers() {
  const { orgId } = Route.useParams()
  const { metadata, queryClient } = Route.useRouteContext()
  const [viewUserOpen, setViewUserOpen] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [invitationToRevoke, setInvitationToRevoke] =
    useState<OrganisationInvitation | null>(null)
  const [invitationToEditGroups, setInvitationToEditGroups] =
    useState<OrganisationInvitation | null>(null)

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<
    (OrganisationUser & { id: string }) | null
  >(null)
  const [userToDeleteFullName, setUserToDeleteFullName] = useState<
    string | null
  >(null)

  const { organisationClient, userClient } = useClient()
  const invitations = useOrgInvitations(orgId, metadata)

  // Groups an invitee can be added to on accept (#3079). Adding them is a
  // group-membership write, so an admin without it gets membership-only
  // invites and no picker. Shares the `['groups', orgId]` cache with the
  // projects page's access pickers.
  const { hasPermission } = usePermissionCheck()
  const canInviteToGroups = hasPermission(Permission.CREATE_GROUP_USER)
  const { data: orgGroups = [] } = useQuery({
    queryKey: ['groups', orgId],
    queryFn: () => fetchGroups(organisationClient, orgId, metadata),
    enabled: canInviteToGroups,
  })
  const groupOptions = useMemo(
    () =>
      orgGroups
        .map((g) => ({ id: g.id, name: g.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [orgGroups],
  )

  // Fetch userMap for displaying user names in createdBy/updatedBy columns
  const { userMap } = useOrganisationUsersWithUserMap({
    orgId,
    organisationClient,
    userClient,
    metadata,
  })

  const orgUsersQueryKey = useMemo(
    () => ['organisationUsers', orgId] as const,
    [orgId],
  )

  // Fetch organisation users with pagination
  const {
    data: orgUsers,
    isLoading: isLoadingOrgUsers,
    pagination,
    refresh: refreshPagination,
  } = useOffsetPaginationQuery<OrgUserWithId>({
    preferenceKey: PREFERENCE_KEY,
    queryKey: orgUsersQueryKey,
    queryFn: async ({ page, pageSize }) => {
      const result = await fetchOrganisationUsersPaginated(
        orgId,
        organisationClient,
        metadata,
        { page, pageSize },
      )
      return {
        ...result,
        items: result.items.map((u) => ({
          ...u,
          id: getOrganisationUserId(u),
        })),
      }
    },
  })

  const { data: user } = useQuery({
    queryKey: ['organisationUser', selectedUserId, orgId],
    queryFn: () =>
      organisationClient.getOrganisationUser(
        { orgId, userId: selectedUserId },
        metadata,
      ),
    enabled: !!selectedUserId,
  })

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await organisationClient.deleteOrganisationUser(
        { orgId, userId },
        metadata,
      )
    },
    onSuccess: () => {
      refreshPagination()
      setDeleteDialogOpen(false)
      setUserToDelete(null)
      setUserToDeleteFullName(null)
    },
  })

  const updateUserMutation = useMutation({
    mutationFn: async (input: UpdateOrganisationUserInput) =>
      organisationClient.updateOrganisationUser(input, metadata),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organisationUsers', orgId] })
    },
  })

  const handleViewUser = async (userId: string) => {
    setSelectedUserId(userId)
    setViewUserOpen(true)
  }

  const handleViewUserModal = async (event: React.FormEvent) => {
    event.preventDefault()
    setViewUserOpen(false)
  }

  const closeInvite = () => {
    setInviteOpen(false)
    invitations.invite.reset()
    invitations.updateGroups.reset()
  }

  const pendingInvitationAction = invitations.resend.isPending
    ? invitations.resend.variables && {
        invitationId: invitations.resend.variables.id,
        action: 'resend' as const,
      }
    : invitations.revoke.isPending
      ? invitations.revoke.variables && {
          invitationId: invitations.revoke.variables.id,
          action: 'revoke' as const,
        }
      : undefined

  const closeEditGroups = () => {
    setInvitationToEditGroups(null)
    invitations.updateGroups.reset()
  }

  const inviteOutcome = invitations.invite.isSuccess
    ? invitations.invite.data
    : undefined

  const invitationActionError = invitations.resend.isError
    ? orgInvitationErrorMessage(invitations.resend.error, 'resend')
    : invitations.revoke.isError
      ? orgInvitationErrorMessage(invitations.revoke.error, 'revoke')
      : undefined

  // Handler to trigger dialog
  const handleDeleteUser = (user: OrganisationUser & { id: string }) => {
    setUserToDeleteFullName(getUserFullName(userMap, user.userId))
    setUserToDelete(user)
    setDeleteDialogOpen(true)
  }

  // Handler to confirm deletion
  const confirmDeleteUser = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.userId)
    }
  }

  // Handler to cancel deletion
  const cancelDeleteUser = () => {
    setDeleteDialogOpen(false)
    setUserToDelete(null)
  }

  const Inputs = [
    { id: 'userId', label: 'User ID', value: user?.userId },
    { id: 'isActive', label: 'Active', value: user?.isActive ? 'Yes' : 'No' },
    { id: 'created', label: 'Created', value: user?.created?.toString() },
    { id: 'createdBy', label: 'Created By', value: user?.createdBy },
    { id: 'updated', label: 'Updated', value: user?.updated?.toString() },
    { id: 'updatedBy', label: 'Updated By', value: user?.updatedBy },
  ]

  const renderInputs = () => {
    return Inputs.map((field) => (
      <div key={field.id}>
        <Input
          label={field.label}
          id={field.id}
          name={field.id}
          type="text"
          readOnly={true}
          value={field.value}
        />
      </div>
    ))
  }

  return (
    <div>
      {/* Invite Member Modal (#3059) — replaces the old direct add */}
      <Modal
        title="Invite member"
        description="Invite someone to this organisation by email. They’ll be asked to accept, and can sign up first if they don’t have an account."
        icon={<EnvelopeIcon />}
        isOpen={inviteOpen}
        onClose={closeInvite}
      >
        <InviteMemberForm
          onSubmit={(email, groupIds) =>
            invitations.invite.mutate({ email, groupIds })
          }
          groups={canInviteToGroups ? groupOptions : []}
          onCancel={closeInvite}
          isPending={invitations.invite.isPending}
          sentTo={
            inviteOutcome?.kind === 'sent'
              ? inviteOutcome.invitation.email
              : undefined
          }
          // An admin who can't add group members can't change the open
          // invitation's groups either, so a groups conflict is, for them,
          // just "already invited".
          alreadyInvited={
            inviteOutcome?.kind === 'alreadyOpen' ||
            (inviteOutcome?.kind === 'groupsDiffer' && !canInviteToGroups)
              ? inviteOutcome.invitation
              : undefined
          }
          groupsDiffer={
            inviteOutcome?.kind === 'groupsDiffer' &&
            canInviteToGroups &&
            !invitations.updateGroups.isSuccess
              ? {
                  email: inviteOutcome.invitation.email,
                  currentGroupNames: invitationGroupNames(
                    inviteOutcome.invitation.groupIds,
                    groupOptions,
                  ),
                  requestedGroupNames: invitationGroupNames(
                    inviteOutcome.requestedGroupIds,
                    groupOptions,
                  ),
                }
              : undefined
          }
          onUpdateGroups={() => {
            if (inviteOutcome?.kind !== 'groupsDiffer') return
            invitations.updateGroups.mutate({
              invitation: inviteOutcome.invitation,
              groupIds: inviteOutcome.requestedGroupIds,
            })
          }}
          isUpdatingGroups={invitations.updateGroups.isPending}
          groupsUpdatedFor={
            invitations.updateGroups.isSuccess
              ? invitations.updateGroups.data.email
              : undefined
          }
          error={
            invitations.invite.isError
              ? orgInvitationErrorMessage(invitations.invite.error, 'invite')
              : invitations.updateGroups.isError
                ? orgInvitationErrorMessage(
                    invitations.updateGroups.error,
                    'updateGroups',
                  )
                : undefined
          }
          onInviteAnother={() => {
            invitations.invite.reset()
            invitations.updateGroups.reset()
          }}
          onEmailChange={() => {
            if (invitations.invite.isError) invitations.invite.reset()
          }}
        />
      </Modal>

      {/* Change an open invitation's groups (#3079) */}
      <Modal
        title="Change invitation groups"
        icon={<PencilSquareIcon />}
        isOpen={invitationToEditGroups !== null}
        onClose={closeEditGroups}
      >
        {invitationToEditGroups && (
          <EditInvitationGroupsForm
            invitation={invitationToEditGroups}
            groups={groupOptions}
            onSave={(groupIds) =>
              invitations.updateGroups.mutate(
                { invitation: invitationToEditGroups, groupIds },
                { onSuccess: closeEditGroups },
              )
            }
            onCancel={closeEditGroups}
            isPending={invitations.updateGroups.isPending}
            error={
              invitations.updateGroups.isError
                ? orgInvitationErrorMessage(
                    invitations.updateGroups.error,
                    'updateGroups',
                  )
                : undefined
            }
          />
        )}
      </Modal>

      {/* View User Modal */}
      <Modal
        title="View Organisation Member"
        icon={<PencilSquareIcon />}
        isOpen={viewUserOpen}
        onClose={() => setViewUserOpen(false)}
      >
        <form onSubmit={handleViewUserModal} className="flex-col space-y-6">
          {renderInputs()}
          <div className="mt-5 flex flex-row-reverse gap-2 sm:mt-4">
            <Button type="submit">Close</Button>
          </div>
        </form>
      </Modal>

      {/* Organisation Users Table */}
      <div>
        <DataTable<OrganisationUser & { id: string }>
          data={orgUsers}
          columns={userColumns}
          title="Organisation Members"
          description="DroidMap members who can participate in this organisation. Invite new members by email."
          isLoading={isLoadingOrgUsers}
          onCreate={() => setInviteOpen(true)}
          createButtonText="Invite"
          paginationConfig={pagination}
          tableOptions={{
            meta: {
              userMap,
              actions: {
                onDelete: handleDeleteUser,
                onUpdate: (user: OrganisationUser & { id: string }) =>
                  updateUserMutation.mutate({
                    orgId: user.orgId,
                    userId: user.userId,
                    isActive: user.isActive,
                  }),
                onView: (user: OrganisationUser & { id: string }) =>
                  handleViewUser(user.userId),
              },
              hasError: updateUserMutation.isError,
            },
          }}
        />
      </div>
      <PendingInvitationsTable
        invitations={invitations.query.data ?? []}
        isLoading={invitations.query.isLoading}
        isError={invitations.query.isError}
        onRetry={() => invitations.query.refetch()}
        inviterName={(userId) => getUserFullName(userMap, userId)}
        groups={canInviteToGroups ? groupOptions : []}
        onEditGroups={(invitation) => {
          invitations.resend.reset()
          invitations.revoke.reset()
          invitations.updateGroups.reset()
          setInvitationToEditGroups(invitation)
        }}
        onResend={(invitation) => {
          invitations.revoke.reset()
          invitations.resend.mutate(invitation)
        }}
        onRevoke={(invitation) => {
          invitations.resend.reset()
          setInvitationToRevoke(invitation)
        }}
        pending={pendingInvitationAction}
        error={invitationActionError}
        notice={
          invitations.resend.isSuccess
            ? `Invitation resent to ${invitations.resend.data.email}.`
            : undefined
        }
      />
      <ConfirmationDialog
        title="Revoke invitation"
        description={
          <>
            <strong className="font-semibold text-text">
              {invitationToRevoke?.email}
            </strong>{' '}
            will no longer be able to accept this invitation. You can invite
            them again later.
          </>
        }
        isOpen={invitationToRevoke !== null}
        onClose={() => setInvitationToRevoke(null)}
        onConfirm={() => {
          if (!invitationToRevoke) return
          invitations.revoke.mutate(invitationToRevoke, {
            onSettled: () => setInvitationToRevoke(null),
          })
        }}
        confirmText="Revoke"
        cancelText="Cancel"
        variant="destructive"
        isLoading={invitations.revoke.isPending}
      />
      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        title="Remove Organisation Member"
        description={
          <>
            You&rsquo;re about to remove{' '}
            <strong className="font-semibold text-text">
              {userToDeleteFullName}
            </strong>{' '}
            from this organisation. They&rsquo;ll lose access to all projects
            and assets immediately. They can be invited back later.
          </>
        }
        isOpen={deleteDialogOpen}
        onClose={cancelDeleteUser}
        onConfirm={confirmDeleteUser}
        confirmText="Remove"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteUserMutation.isPending}
      />
    </div>
  )
}
